<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Top bar -->
    <nav class="bg-white shadow-sm px-6 py-3 flex justify-between items-center">
      <div class="flex items-center space-x-4">
        <img src="/assets/logo.png" alt="AMGO" class="h-10" />
        <h1 class="text-lg font-bold text-gray-800">Administration</h1>
      </div>
      <div class="flex items-center space-x-4">
        <span class="text-sm text-gray-600">{{ adminName }}</span>
        <button @click="logout" class="text-sm text-red-600 hover:underline">
          Déconnexion
        </button>
      </div>
    </nav>

    <div class="max-w-6xl mx-auto px-4 py-8">
      <!-- Header -->
      <div class="flex justify-between items-center mb-8">
        <h2 class="text-2xl font-bold text-gray-900">Gestion des Actualités</h2>
        <button
          @click="
            showForm = true;
            editingId = null;
            resetForm();
          "
          class="bg-indigo-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
        >
          + Nouvelle actualité
        </button>
      </div>

      <!-- Form modal -->
      <div
        v-if="showForm"
        class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center overflow-y-auto py-8"
      >
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4">
          <div
            class="p-2 bg-blue-100 border-b flex justify-between items-center"
          >
            <h3 class="text-xl font-bold">
              {{ editingId ? "Modifier" : "Nouvelle" }} actualité
            </h3>
            <button
              @click="showForm = false"
              class="text-gray-400 hover:text-gray-600 text-2xl"
            >
              &times;
            </button>
          </div>

          <form @submit.prevent="saveActualite" class="p-6 space-y-5">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Titre *</label
              >
              <input
                v-model="form.title"
                required
                class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Date</label
                >
                <input
                  v-model="form.date"
                  class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="ex: 15 et 16 mai 2025"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Lieu</label
                >
                <input
                  v-model="form.lieu"
                  class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="ex: Faculté de Médecine d'Oran"
                />
              </div>
            </div>

            <div class="grid grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Icône</label
                >
                <input
                  v-model="form.icon"
                  class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="ex: 🩺"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Couleur</label
                >
                <select
                  v-model="form.color"
                  class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="blue">Bleu</option>
                  <option value="green">Vert</option>
                  <option value="red">Rouge</option>
                  <option value="yellow">Jaune</option>
                  <option value="purple">Violet</option>
                </select>
              </div>
              <div class="flex items-end">
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="form.published"
                    class="w-4 h-4 text-indigo-600 rounded"
                  />
                  <span class="text-sm font-medium text-gray-700">Publié</span>
                </label>
              </div>
            </div>

            <div>
              <label class="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="form.is_announcement"
                  class="w-4 h-4 text-indigo-600 rounded"
                />
                <span class="text-sm font-medium text-gray-700"
                  >Faire apparaitre dans Annonces</span
                >
              </label>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Résumé</label
              >
              <textarea
                v-model="form.sub_description"
                rows="3"
                class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              ></textarea>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Contenu (HTML)</label
              >
              <editor v-model="form.description" :init="editorSettings" />
            </div>

            <!-- Videos section -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Vidéos YouTube</label
              >
              <div
                v-for="(vid, i) in form.videos"
                :key="`vid-${i}`"
                class="flex items-center gap-2 mb-2"
              >
                <img
                  v-if="ytThumb(vid.url)"
                  :src="ytThumb(vid.url)"
                  class="w-20 h-12 object-cover rounded"
                />
                <input
                  v-model="vid.url"
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  class="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none text-sm"
                />
                <button
                  type="button"
                  @click="removeVideo(i)"
                  class="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600"
                >
                  &times;
                </button>
              </div>
              <button
                type="button"
                @click="addVideo"
                class="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                + Ajouter une vidéo
              </button>
            </div>

            <!-- Images section -->
            <div v-if="editingId">
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Images</label
              >
              <div class="flex flex-wrap gap-3 mb-3">
                <div
                  v-for="img in currentImages"
                  :key="img.id"
                  class="relative w-24 h-24"
                >
                  <img
                    :src="getImageUrl(img.url)"
                    class="w-full h-full object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    @click="deleteImage(img.id)"
                    class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                  >
                    &times;
                  </button>
                </div>
              </div>
              <input
                type="file"
                ref="imageInput"
                multiple
                accept="image/*"
                @change="uploadImages"
                class="text-sm"
              />
            </div>

            <div class="text-red-600 text-sm" v-if="formError">
              {{ formError }}
            </div>

            <div class="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                @click="showForm = false"
                class="px-5 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                :disabled="saving"
                class="px-5 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {{
                  saving
                    ? "Enregistrement..."
                    : editingId
                    ? "Modifier"
                    : "Créer"
                }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Delete confirmation -->
      <div
        v-if="deletingId"
        class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
      >
        <div class="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4">
          <h3 class="text-lg font-bold text-gray-900 mb-2">
            Confirmer la suppression
          </h3>
          <p class="text-gray-600 mb-6">
            Cette action est irréversible. L'actualité et toutes ses images
            seront supprimées.
          </p>
          <div class="flex justify-end space-x-3">
            <button
              @click="deletingId = null"
              class="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              @click="confirmDelete"
              class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Supprimer
            </button>
          </div>
        </div>
      </div>

      <!-- Actualités list -->
      <div class="space-y-4">
        <div v-if="loadingList" class="text-center py-12 text-gray-500">
          Chargement...
        </div>

        <div
          v-else-if="actualites.length === 0"
          class="text-center py-12 text-gray-400"
        >
          Aucune actualité. Créez-en une !
        </div>

        <draggable
          v-if="actualites.length > 0"
          v-model="actualites"
          item-key="id"
          handle=".drag-handle"
          class="space-y-4"
          @end="onReorder"
        >
          <template #item="{ element: actu }">
            <div
              class="bg-white rounded-lg shadow-sm p-5 flex items-start justify-between hover:shadow-md transition-shadow"
            >
              <div class="flex items-start space-x-4 flex-1 min-w-0">
                <span
                  class="drag-handle cursor-grab text-gray-400 hover:text-gray-600 select-none text-2xl flex-shrink-0 leading-none"
                  title="Glisser pour réordonner"
                  >⠿</span
                >
                <div class="text-3xl flex-shrink-0">{{ actu.icon }}</div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center space-x-2 mb-1">
                    <h3 class="font-semibold text-gray-900 truncate">
                      {{ actu.title }}
                    </h3>
                    <span
                      v-if="!actu.published"
                      class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex-shrink-0"
                      >Brouillon</span
                    >
                  </div>
                  <p class="text-sm text-gray-500">
                    {{ actu.date }} &mdash; {{ actu.lieu }}
                  </p>
                  <p class="text-sm text-gray-400 mt-1">
                    {{ actu.images.length }} image(s),
                    {{ actu.videos.length }} vidéo(s)
                  </p>
                </div>
              </div>
              <div class="flex items-center space-x-2 ml-4 flex-shrink-0">
                <button
                  @click="editActualite(actu)"
                  class="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                  Modifier
                </button>
                <button
                  @click="deletingId = actu.id"
                  class="text-red-500 hover:text-red-700 text-sm font-medium"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </template>
        </draggable>
      </div>
    </div>

    <!-- Claude Optimization Modal -->
    <div
      v-if="showOptimizeModal"
      class="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4"
    >
      <div
        class="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
      >
        <div class="p-4 border-b flex justify-between items-center">
          <h3 class="text-lg font-bold flex items-center">
            <span class="mr-2">✨</span>Optimisation Claude
          </h3>
          <button
            @click="closeOptimizeModal"
            class="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Fermer"
          >
            &times;
          </button>
        </div>
        <div class="p-6 overflow-y-auto flex-1">
          <div v-if="optimizing" class="text-center py-12">
            <div
              class="inline-block w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"
            ></div>
            <p class="mt-4 text-gray-600">
              Optimisation en cours… (cela peut prendre 10-30s)
            </p>
          </div>
          <div
            v-else-if="optimizeError"
            class="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4"
          >
            <p class="font-semibold mb-1">Erreur</p>
            <p class="text-sm">{{ optimizeError }}</p>
          </div>
          <div v-else>
            <p class="text-sm text-gray-600 mb-3">Aperçu du texte optimisé :</p>
            <div
              class="border rounded-lg p-4 bg-gray-50"
              v-html="optimizedHtml"
            ></div>
            <div
              v-if="optimizeUsage"
              class="mt-3 text-xs text-gray-500"
            >
              Tokens : entrée {{ optimizeUsage.input_tokens }}, sortie
              {{ optimizeUsage.output_tokens }}<span
                v-if="optimizeUsage.cache_read_input_tokens"
              >
                (cache lu : {{ optimizeUsage.cache_read_input_tokens }})</span
              >
            </div>
          </div>
        </div>
        <div
          v-if="!optimizing"
          class="p-4 border-t flex justify-end space-x-2"
        >
          <button
            @click="closeOptimizeModal"
            class="px-5 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            v-if="!optimizeError && optimizedHtml"
            @click="acceptOptimization"
            class="px-5 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
          >
            Accepter et remplacer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Editor from "@tinymce/tinymce-vue";
import draggable from "vuedraggable";
import { youtubeThumbnail } from "../../utils/youtube";

import "tinymce/tinymce";

import "tinymce/models/dom/model";
import "tinymce/themes/silver";
import "tinymce/icons/default";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/code";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/lists";
import "tinymce/plugins/advlist";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/skins/ui/oxide/content.min.css";
import "tinymce/skins/content/default/content.min.css";

export default {
  name: "AdminDashboard",
  components: { Editor, draggable },
  data() {
    return {
      actualites: [],
      loadingList: true,
      showForm: false,
      saving: false,
      formError: "",
      editingId: null,
      deletingId: null,
      currentImages: [],
      form: this.emptyForm(),
      content: "",
      editorSettings: {
        height: 450,
        language: "fr_FR",
        language_url:
          "https://cdn.jsdelivr.net/npm/tinymce-i18n@latest/langs/fr_FR.js",
        menubar: true,
        plugins: ["fullscreen", "image", "code", "lists", "advlist", "link"],
        toolbar:
          "claudeoptimize | fullscreen | bullist numlist outdent indent | link image | code " +
          "undo redo | blocks fontfamily fontsize | " +
          "bold italic underline forecolor backcolor | " +
          "alignleft aligncenter alignright alignjustify | ",
        automatic_uploads: true,
        paste_data_images: true,
        images_dataimg_filter: (img) => !img.hasAttribute("data-mce-bogus"),
        images_upload_handler: (blobInfo) =>
          new Promise((resolve, reject) => {
            const file = blobInfo.blob();
            if (!file.type.startsWith("image/")) {
              return reject({ message: "Only images are allowed", remove: true });
            }
            if (file.size > 2 * 1024 * 1024) {
              return reject({ message: "Image too large (max 2MB)", remove: true });
            }
            resolve(`data:${file.type};base64,${blobInfo.base64()}`);
          }),
        setup: (editor) => {
          editor.ui.registry.addButton("claudeoptimize", {
            text: "✨ Claude",
            tooltip: "Optimiser le texte avec Claude (mise en forme HTML AMGO)",
            onAction: () => {
              this.optimizeWithClaude(editor);
            },
          });
        },
      },
      optimizeEditor: null,
      showOptimizeModal: false,
      optimizing: false,
      optimizeError: null,
      optimizedHtml: "",
      optimizeUsage: null,
    };
  },
  computed: {
    adminName() {
      try {
        return (
          JSON.parse(localStorage.getItem("admin_user") || "{}").name || "Admin"
        );
      } catch {
        return "Admin";
      }
    },
    token() {
      return localStorage.getItem("admin_token");
    },
  },
  mounted() {
    if (!this.token) {
      this.$router.push("/admin");
      return;
    }
    this.fetchActualites();
  },
  methods: {
    getImageUrl(url) {
      if (!url) return "";

      if (url.includes("/assets/uploads/")) {
        return `${process.env.VUE_APP_HSERVER_URL}/amgo${url}`;
      }

      return url;
    },
    emptyForm() {
      return {
        title: "",
        sub_description: "",
        description: "",
        date: "",
        lieu: "",
        icon: "📰",
        color: "blue",
        component: "",
        published: true,
        is_announcement: false,
        videos: [],
      };
    },
    resetForm() {
      this.form = this.emptyForm();
      this.currentImages = [];
      this.formError = "";
    },
    authHeaders() {
      return {
        Authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      };
    },
    async fetchActualites() {
      this.loadingList = true;
      try {
        const res = await fetch(
          `${process.env.VUE_APP_HSERVER_URL}/api/actualites/admin/all`,
          {
            headers: this.authHeaders(),
          },
        );
        if (res.status === 401) {
          this.logout();
          return;
        }
        this.actualites = await res.json();
      } catch (err) {
        this.formError = "Erreur de chargement";
      } finally {
        this.loadingList = false;
      }
    },
    editActualite(actu) {
      this.editingId = actu.id;
      this.form = {
        title: actu.title,
        sub_description: actu.sub_description,
        description: actu.description,
        date: actu.date,
        lieu: actu.lieu,
        icon: actu.icon,
        color: actu.color,
        component: actu.component,
        published: !!actu.published,
        is_announcement: !!actu.is_announcement,
        videos: actu.videos || [],
      };
      this.currentImages = [...(actu.images || [])];
      this.showForm = true;
    },
    async saveActualite() {
      this.formError = "";
      this.saving = true;
      try {
        const url = this.editingId
          ? `${process.env.VUE_APP_HSERVER_URL}/api/actualites/${this.editingId}`
          : `${process.env.VUE_APP_HSERVER_URL}/api/actualites`;
        const method = this.editingId ? "PUT" : "POST";

        const res = await fetch(url, {
          method,
          headers: this.authHeaders(),
          body: JSON.stringify(this.form),
        });

        if (!res.ok) {
          const data = await res.json();
          this.formError = data.error || "Erreur lors de l'enregistrement";
          return;
        }

        this.showForm = false;
        this.resetForm();
        await this.fetchActualites();
      } catch (err) {
        this.formError = "Erreur de connexion au serveur";
      } finally {
        this.saving = false;
      }
    },
    async optimizeWithClaude(editor) {
      const text = editor.getContent({ format: "text" }).trim();
      if (!text) {
        alert("Saisissez d'abord du texte dans l'éditeur");
        return;
      }
      this.optimizeEditor = editor;
      this.showOptimizeModal = true;
      this.optimizing = true;
      this.optimizeError = null;
      this.optimizedHtml = "";
      this.optimizeUsage = null;
      try {
        const res = await fetch(
          `${process.env.VUE_APP_HSERVER_URL}/api/ai/optimize`,
          {
            method: "POST",
            headers: this.authHeaders(),
            body: JSON.stringify({ text }),
          },
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || `Erreur ${res.status}`);
        }
        this.optimizedHtml = data.optimized || "";
        this.optimizeUsage = data.usage || null;
      } catch (err) {
        this.optimizeError = err.message;
      } finally {
        this.optimizing = false;
      }
    },
    acceptOptimization() {
      if (this.optimizeEditor && this.optimizedHtml) {
        this.optimizeEditor.setContent(this.optimizedHtml);
        this.form.description = this.optimizedHtml;
      }
      this.closeOptimizeModal();
    },
    closeOptimizeModal() {
      this.showOptimizeModal = false;
      this.optimizing = false;
      this.optimizeError = null;
      this.optimizedHtml = "";
      this.optimizeEditor = null;
      this.optimizeUsage = null;
    },
    addVideo() {
      this.form.videos.push({ url: "", title: "", subtitle: "", image: "" });
    },
    removeVideo(i) {
      this.form.videos.splice(i, 1);
    },
    ytThumb(url) {
      return youtubeThumbnail(url, "default");
    },
    async onReorder() {
      try {
        const ids = this.actualites.map((a) => a.id);
        await fetch(
          `${process.env.VUE_APP_HSERVER_URL}/api/actualites/reorder`,
          {
            method: "PUT",
            headers: this.authHeaders(),
            body: JSON.stringify({ ids }),
          },
        );
      } catch (err) {
        this.formError = "Erreur lors du réordonnement";
      }
    },
    async confirmDelete() {
      try {
        await fetch(
          `${process.env.VUE_APP_HSERVER_URL}/api/actualites/${this.deletingId}`,
          {
            method: "DELETE",
            headers: this.authHeaders(),
          },
        );
        this.deletingId = null;
        await this.fetchActualites();
      } catch (err) {
        this.formError = "Erreur lors de la suppression";
        this.deletingId = null;
      }
    },
    async uploadImages() {
      const files = this.$refs.imageInput.files;
      if (!files.length || !this.editingId) return;

      const formData = new FormData();
      for (const file of files) {
        formData.append("images", file);
      }

      try {
        const res = await fetch(
          `${process.env.VUE_APP_HSERVER_URL}/api/actualites/${this.editingId}/images`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${this.token}` },
            body: formData,
          },
        );

        if (res.ok) {
          const data = await res.json();
          data.images.forEach((img) => {
            this.currentImages.push({
              id: Date.now() + Math.random(),
              url: img.url,
            });
          });
          this.$refs.imageInput.value = "";
          await this.fetchActualites();
        }
      } catch (err) {
        this.formError = "Erreur lors de l'upload";
      }
    },
    async deleteImage(imageId) {
      try {
        await fetch(
          `${process.env.VUE_APP_HSERVER_URL}/api/actualites/${this.editingId}/images/${imageId}`,
          { method: "DELETE", headers: this.authHeaders() },
        );
        this.currentImages = this.currentImages.filter(
          (img) => img.id !== imageId,
        );
        await this.fetchActualites();
      } catch (err) {
        this.formError = "Erreur lors de la suppression de l'image";
      }
    },
    logout() {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
      this.$router.push("/admin");
    },
  },
};
</script>
<style>
.tox-statusbar {
  display: none !important;
}
</style>
